import React, { useState } from 'react';
import { useLanguage } from '../../../hooks/useLanguage';
import { header as frHeader } from '../../../locales/fr/header';
import { header as arHeader } from '../../../locales/ar/header';
import { header as esHeader } from '../../../locales/es/header';
import Modal from '../../../components/Common/Modal';
import { registrationService } from '../../../services/mysql/registration.service';
import { db } from '../../../services/mysql/data_mysqlbase';
import { useLiveQuery } from 'dexie-react-hooks';
import { initService } from '../../../services/AppDataServices/init.service';

const translations = {
  fr: frHeader,
  ar: arHeader,
  es: esHeader
};

const RegisterModal = ({ isOpen, onClose, onSubmit }) => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    city: '',
    businessType: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Exemple d'utilisation de useLiveQuery pour afficher les inscriptions
  const registrations = useLiveQuery(
    () => db.registrations.orderBy('createdAt').reverse().toArray(),
    [], // Dépendances (vide car on veut toujours les dernières données)
    [] // Valeur par défaut
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Sauvegarder dans IndexedDB
      const registrationId = await registrationService.saveRegistration(formData);
      console.log('Registration saved with ID:', registrationId);

      // Initialiser la base de données osc_data
      try {
        const dbInitialized = await initService.initDatabase();
        if (!dbInitialized) {
          console.log('Base de données déjà initialisée');
        }
      } catch (initError) {
        console.error('Erreur d\'initialisation:', initError);
        // On continue même si l'initialisation échoue
      }

      // Appeler le callback onSubmit si fourni
      if (onSubmit) {
        onSubmit(formData);
      }

      // Fermer le modal
      onClose();
    } catch (err) {
      console.error('Error saving registration:', err);
      setError('Une erreur est survenue lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t.auth.register}
      size="lg"
    >
      <div className="bg-white rounded-lg p-6">
        <div className="text-center mb-8">
          <p className="text-gray-600">{t.auth.fillForm}</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-1">
              {t.auth.fullName} *
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
              placeholder={t.auth.fullNamePlaceholder}
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-1">
                {t.auth.phone} *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                placeholder={t.auth.phonePlaceholder}
              />
            </div>

            <div>
              <label htmlFor="city" className="block text-sm font-semibold text-gray-700 mb-1">
                {t.auth.city} *
              </label>
              <select
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
              >
                <option value="">{t.auth.selectCity}</option>
                <option value="casablanca">{t.cities.casablanca}</option>
                <option value="rabat">{t.cities.rabat}</option>
                <option value="marrakech">{t.cities.marrakech}</option>
                <option value="fes">{t.cities.fes}</option>
                <option value="tanger">{t.cities.tanger}</option>
                <option value="agadir">{t.cities.agadir}</option>
                <option value="meknes">{t.cities.meknes}</option>
                <option value="oujda">{t.cities.oujda}</option>
                <option value="kenitra">{t.cities.kenitra}</option>
                <option value="tetouan">{t.cities.tetouan}</option>
                <option value="sale">{t.cities.sale}</option>
                <option value="nador">{t.cities.nador}</option>
                <option value="mohammedia">{t.cities.mohammedia}</option>
                <option value="eljadida">{t.cities.eljadida}</option>
                <option value="benimellal">{t.cities.benimellal}</option>
                <option value="autre">{t.cities.other}</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="businessType" className="block text-sm font-semibold text-gray-700 mb-1">
              {t.auth.businessType} *
            </label>
            <select
              id="businessType"
              name="businessType"
              value={formData.businessType}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
            >
              <option value="">{t.auth.selectBusinessType}</option>
              <option value="epicerie">{t.businessTypes.epicerie}</option>
              <option value="parapharmacie">{t.businessTypes.parapharmacie}</option>
              <option value="cafe_restaurant">{t.businessTypes.cafe_restaurant}</option>
              <option value="boulangerie_patisserie">{t.businessTypes.boulangerie_patisserie}</option>
              <option value="meule">{t.businessTypes.meule}</option>
              <option value="vetement">{t.businessTypes.vetement}</option>
              <option value="atelier">{t.businessTypes.atelier}</option>
              <option value="service_maintenance">{t.businessTypes.service_maintenance}</option>
            </select>
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200"
            >
              {t.common.cancel}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 disabled:opacity-50"
            >
              {isSubmitting ? t.common.sending : t.common.send}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default RegisterModal; 